import { ThrowStmt } from '@angular/compiler';
import { Component, OnInit, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { DialogService } from 'src/app/service/dialog/dialog.service';
import { LoaderService } from 'src/app/service/shared/loader/loader.service';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { JobPointeTransferService } from '../../job-pointe-transfer/job-pointe-transfer.service';
import { JobBoardService } from '../service/job-board.service';
import { JobPointeService } from 'src/app/service/job-pointe/job-pointe.service';
import { PermissionsService } from 'src/app/service/auth/permissions.service';


type EmployeeAction = {
  [key: string]: Function
}

@Component({
  selector: 'app-job-employee-card',
  templateUrl: './job-employee-card.component.html',
  styleUrls: ['./job-employee-card.component.scss'],
})
export class JobEmployeeCardComponent implements OnInit {
  @Input() employee: any;
  @Input() job: any;
  @Input() tagAssignments: Array<any> = [];
  @Output() openEmployeeSideNav = new EventEmitter<any>();
  @Output() onEraseTag = new EventEmitter<any>();
  @ViewChild('employeeCard')
  employeeCard: any;
  employeeFormatShapes: Array<any> = [];
  isEmployeeSideNavOpen: boolean = true;
  jobBoardLocked: boolean = false;
  jobBoardLockedSubscription: Subscription;
  formatShapeSelectedSubscription: Subscription;
  selectedFormatShape: any;
  selectedEmployeeCards: Array<any> = [];

  constructor(
    private jobPointeService: JobPointeService,
    private jobBoardService: JobBoardService,
    private toastService: ToastService,
    private jobPointeTransferService: JobPointeTransferService,
    private sanitizer: DomSanitizer,
    private dialogService: DialogService,
    private permissionsService: PermissionsService,
  ) { }

  statusClassName: string;
  statusLabel: string;
  facilityJobAssignmentSelection: any;
  facilityJobAssignmentSelectedSubscription: Subscription;
  selectedEmployeeCardsSubscription: Subscription;

  ngOnInit(): void {
    this.assignStatusClass();
    this.jobBoardLockedSubscription = this.jobBoardService.jobBoardLockedSource.subscribe(res => {
      this.jobBoardLocked = res;
    });
    if (this.jobBoardService.formatShapeSelectedSource) {
      this.formatShapeSelectedSubscription = this.jobBoardService.formatShapeSelectedSource.subscribe(res => {
        this.selectedFormatShape = res;
      });
    }
    this.facilityJobAssignmentSelectedSubscription = this.jobBoardService.facilityJobAssignmentSelectedSource.subscribe(
      res => {
        this.facilityJobAssignmentSelection = res;
      },
    );
    this.selectedEmployeeCardsSubscription = this.jobBoardService.selectedEmployeeCardsSource.subscribe(res => {
      this.selectedEmployeeCards = res;
    });
  }

  ngOnDestroy() {
    this.jobBoardLockedSubscription.unsubscribe();
    if (this.formatShapeSelectedSubscription) this.formatShapeSelectedSubscription.unsubscribe();
    if (this.facilityJobAssignmentSelectedSubscription) this.facilityJobAssignmentSelectedSubscription.unsubscribe();
    if (this.selectedEmployeeCardsSubscription) this.selectedEmployeeCardsSubscription.unsubscribe();
  }


  employeeActions: EmployeeAction = {
    viewProfile: this.viewProfile.bind(this),
    move: this.reassignEmployee.bind(this),
    copy: this.assignEmployee.bind(this),
    remove: this.removeEmployee.bind(this),
  };

  employeeAction(actionId: string): void {
    this.employeeActions[actionId]();
  }

  getEmployeeTags() {
    return this.tagAssignments.filter(ta => ta.userId == this.employee.id);
  }

  async removeEmployee() {
    const removeSchedule = {
      userId: this.employee.id,
      ...(this.job.jobNumber && { jobId: this.job.id }),
      ...(this.job.facilityName && { facilityId: this.job.id }),
    };
    try {
      const result = await this.jobBoardService.removeEmployeeSchedule(removeSchedule);

      if (!result?.success) return
      if (this.selectedEmployeeCards?.find(card => card?.id === this.employee?.id)) {
        this.jobBoardService.employeeSideBarOpen.next(null);
        this.jobBoardService.selectedEmployeeCards.next([]);
      }

      if (this.job?.scheduleEmployees) {
        const index = this.job.scheduleEmployees.findIndex(s => s.userId === this.employee.id);
        if (index > -1) {
          this.job.scheduleEmployees.splice(index, 1);
        }
      }

      this.toastService.showToast(result.message, 'success');

      // Intentionally leaving this commented in case client mentions error.
      // this.jobBoardService.updateJobBoard(false);

    } catch (error) {
      this.toastService.showToast('An error occurred removing an employee', 'error');
    }
  }

  getEmployeeThumbnail() {
    const thumb = this.employee?.avatar?.url;
    const fallbackImage = '/assets/images/employee_placeholder.jpg';

    if (!thumb) return fallbackImage;
    return thumb;
  }

  async reassignEmployee() {
    this.jobBoardService.jobBoardDialogAction.next('Move');
    this.dialogService.open('facility-selection-dialog').then(async response => {
      if (response) {
        try {
          let targetCard = this.facilityJobAssignmentSelection?.isSeparateOnJobBoard
            ? this.facilityJobAssignmentSelection
            : this.facilityJobAssignmentSelection?.facility;
          let result = await this.jobPointeTransferService.moveEmployeeToCard(this.employee, this.job, targetCard);
          if (result.success) {
            this.toastService.showToast(
              `${this.employee.employeeAlias} successfully moved to ${targetCard?.facilityName || `${targetCard.jobNumber} - ${targetCard?.name}`
              }`,
              'success',
            );
          } else {
            this.toastService.showToast(result.message, 'error');
          }
        } catch (error) {
          this.toastService.showToast(error.message, 'error');
        }
      }
      this.jobBoardService.facilityJobAssignmentSelected.next(null);
      this.jobBoardService.selectedEmployeeCards.next([]);
    });
  }

  async assignEmployee() {
    this.jobBoardService.jobBoardDialogAction.next('Copy');
    this.dialogService.open('facility-selection-dialog').then(async response => {
      if (response) {
        try {
          const targetCard = this.facilityJobAssignmentSelection?.isSeparateOnJobBoard
            ? this.facilityJobAssignmentSelection
            : this.facilityJobAssignmentSelection?.facility;

          const result = await this.jobPointeTransferService.addEmployeeToCard(this.employee, targetCard, true);

          if (result.success) {
            this.toastService.showToast(
              `${this.employee.employeeAlias} successfully copied to ${targetCard?.facilityName || `${targetCard.jobNumber} - ${targetCard?.name}`
              }`,
              'success',
            );
          } else {
            this.toastService.showToast(result.message, 'error');
          }
        } catch (error) {
          this.toastService.showToast(error.message, 'error');
        }
      }
      this.jobBoardService.facilityJobAssignmentSelected.next(null);
      this.jobBoardService.selectedEmployeeCards.next([]);
    });
  }

  async viewProfile() {
    this.jobPointeTransferService.selectedRowSource.next(this.employee);
    this.jobPointeTransferService.modalIsOpenSource.next(true);
  }

  assignStatusClass() {
    if (this.employee?.status) {
      this.statusClassName = 'status-active';
      this.statusLabel = 'active';
    } else {
      this.statusClassName = 'status-inactive';
      this.statusLabel = 'inactive';
    }
  }

  ngOnChanges(changes) {
    if (!changes?.employee) return;
    if (!this.isSelectedEmployeeCard()) return;

    //Handle updates to employee data to side bar
    this.openEmployeeSideNavBar();
  }

  eraseTag(tagAssignment) {
    if (this.selectedFormatShape?.name == 'eraser') {
      this.onEraseTag.emit(tagAssignment);
    }
  }

  isSelectedEmployeeCard() {
    return this.selectedEmployeeCards?.find(card => card?.id == this.employee.id && card?.jobCard?.id == this.job.id)
      ?.id;
  }

  selectEmployeeCard(singleEmployee = undefined) {
    if (this.selectedFormatShape?.asset) return;

    if (singleEmployee) {
      const employee = { ...singleEmployee };
      employee.jobCard = this.job;
      this.jobBoardService.selectedEmployeeCards.next([employee]);
      this.openEmployeeSideNavBar();
    } else {
      const employee = { ...this.employee };
      employee.jobCard = this.job;
      this.jobBoardService.employeeCardSelected.next(employee);
      this.openEmployeeSideNavBar();
    }
  }

  openEmployeeSideNavBar() {
    const employee = { ...this.employee };
    employee.jobCard = this.job;

    this.jobPointeService.read['employee'](employee.id, {
      embed: 'employeeHomeLocal,employeeJobBoardAttributes',
    }).subscribe(({ data }) => {
      employee.jobBoardAttributes = data?.jobBoardAttributes;
      this.jobBoardService.employeeSideBarOpen.next(employee);
    });
  }

  getBorderColor() {
    return `6px solid ${this.employee?.employeeTitle?.color}`;
  }

  sanitizeSVGString(svg: string) {
    let safeSVG: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(svg);
    return safeSVG;
  }

  getCurrentAssignmentNames(employee): string {
    if (!employee?.jobBoardJobs) return '';

    const facilityNames = [];

    employee.jobBoardJobs.forEach(item => {
      const facilityName = `${item?.facility?.facilityName.trim()}`;
      const jobName = `${item?.job?.jobNumber + ' ' + item?.job?.name}`;
      const name = item?.job ? jobName : facilityName;

      facilityNames.push(name);
    });

    return facilityNames.join(', ');
  }

  isAllowed(resource: string, action: string) {
    const resources = {
      job_board_attributes: 'job_board_attributes',
    };
    const resource1 = resources[resource];
    return this.permissionsService.allow({ resource1, action });
  }
}
